import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Badge,
  useTheme,
  useMediaQuery,
  alpha,
  Fade,
  Zoom,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Stop as StopIcon,
  Save as SaveIcon,
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  Schedule as ScheduleIcon,
  Analytics as AnalyticsIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Send as SendIcon,
  Notifications as NotificationsIcon,
  Assessment as AssessmentIcon,
  Description as DescriptionIcon,
  LocalHospital as LocalHospitalIcon,
  Psychology as PsychologyIcon,
  Medication as MedicationIcon,
  Science as ScienceIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Import enhanced consultation services
import { consultationService } from '../../services/api/enhancedConsultationService';
import { fetchPatients, getPatientDetails } from '../../features/patients/patientSlice';

// Import fixed form components
import PrescriptionTab from '../../components/PrescriptionTab';
import TestsForm from '../../components/TestsForm';
import ReferralsForm from '../../components/ReferralsForm';
import SymptomsForm from '../../components/SymptomsForm';
import DiagnosisManager from '../../components/DiagnosisManager';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: alpha(theme.palette.background.paper, 0.95),
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return theme.palette.info.main;
      case 'in_progress': return theme.palette.warning.main;
      case 'completed': return theme.palette.success.main;
      case 'cancelled': return theme.palette.error.main;
      default: return theme.palette.grey[500];
    }
  };
  
  return {
    backgroundColor: alpha(getStatusColor(status), 0.1),
    color: getStatusColor(status),
    border: `1px solid ${alpha(getStatusColor(status), 0.3)}`,
    fontWeight: 600,
  };
});

const PriorityChip = styled(Chip)(({ theme, priority }) => {
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return theme.palette.error.main;
      case 'high': return theme.palette.warning.main;
      case 'normal': return theme.palette.info.main;
      case 'low': return theme.palette.success.main;
      default: return theme.palette.grey[500];
    }
  };
  
  return {
    backgroundColor: alpha(getPriorityColor(priority), 0.1),
    color: getPriorityColor(priority),
    border: `1px solid ${alpha(getPriorityColor(priority), 0.3)}`,
    fontWeight: 600,
  };
});

const EnhancedConsultation = () => {
  const { appointmentId, patientId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Redux state
  const { data: patients, loading, error, selectedPatient } = useSelector((state) => state.patients);

  // Local state
  const [consultation, setConsultation] = useState(null);
  const [consultationLoading, setConsultationLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [isConsultationActive, setIsConsultationActive] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [autoSaveInterval, setAutoSaveInterval] = useState(null);
  const [autoSaveIndicator, setAutoSaveIndicator] = useState(false);
  const [saveCount, setSaveCount] = useState(0);
  const [testMode, setTestMode] = useState(false); // For testing scenarios
  const [consultationData, setConsultationData] = useState({
    chief_complaint: '',
    history_of_present_illness: '',
    review_of_systems: '',
    physical_examination: '',
    assessment_and_plan: '',
    clinical_notes: '',
    vital_signs: {},
    symptoms: [],
    diagnoses: [],
    medications: [],
    allergies: [],
    // Include old consultation features for backward compatibility
    prescriptions: [], // Prescription medications
    testsOrdered: [], // Laboratory tests ordered
    referrals: [], // Specialist referrals
    notes: '', // General notes (synonym for clinical_notes)
    diagnosis: '', // Single diagnosis (for compatibility)
    follow_up_required: false,
    follow_up_date: null,
    follow_up_notes: '',
    outcome: '',
    patient_satisfaction_score: null,
    doctor_notes: '',
  });

  // Modal states
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [analyticsModalOpen, setAnalyticsModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  // Data states
  const [templates, setTemplates] = useState([]);
  const [consultationTypes, setConsultationTypes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  // Error handling
  const [errorMessage, setErrorMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Consultation steps
  const consultationSteps = [
    { label: 'Pre-Consultation', description: 'Patient preparation and initial assessment' },
    { label: 'Chief Complaint', description: 'Primary reason for visit' },
    { label: 'History & Examination', description: 'Medical history and physical examination' },
    { label: 'Assessment & Plan', description: 'Diagnosis and treatment plan' },
    { label: 'Documentation', description: 'Clinical notes and follow-up' },
    { label: 'Completion', description: 'Final review and completion' },
  ];

  // Tab panels with animation
  const TabPanel = ({ children, value, index, ...other }) => (
    <AnimatePresence mode="wait">
      {value === index && (
        <motion.div
          key={index}
          role="tabpanel"
          id={`consultation-tabpanel-${index}`}
          aria-labelledby={`consultation-tab-${index}`}
          variants={tabVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={{ duration: 0.3 }}
          {...other}
        >
          <Box sx={{ p: 3 }}>{children}</Box>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Load patients
        await dispatch(fetchPatients({ page: 1, page_size: 50 })).unwrap();
        
        // Load patient details if patientId provided
        if (patientId) {
          await dispatch(getPatientDetails(patientId)).unwrap();
        }
        
        // Load consultation types and templates
        const [typesResponse, templatesResponse] = await Promise.all([
          consultationService.getConsultationTypes(),
          consultationService.getTemplates()
        ]);
        
        setConsultationTypes(typesResponse.data || typesResponse);
        setTemplates(templatesResponse.data || templatesResponse);
        
        // Try to find existing consultation for this appointment
        if (appointmentId) {
          try {
            // Search for consultation by appointment
            const consultations = await consultationService.getConsultations({ appointment: appointmentId });
            const consultationsList = consultations.results || consultations.data || consultations || [];
            
            if (consultationsList.length > 0) {
              // Load existing consultation - use the most recent one
              const consultation = Array.isArray(consultationsList) ? consultationsList[0] : consultationsList;
              const consultationId = consultation.id || consultation;
              await loadConsultation(consultationId);
            }
            // If no consultation found, it will be created when user starts consultation
          } catch (error) {
            console.log('No existing consultation found for appointment, will create new one on start:', error);
            // This is fine - consultation will be created on start
          }
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
        setErrorMessage('Failed to load consultation data');
        setSnackbarOpen(true);
      }
    };

    loadInitialData();
  }, [dispatch, appointmentId, patientId]);

  // Load consultation data
  const loadConsultation = async (consultationId) => {
    try {
      setConsultationLoading(true);
      const response = await consultationService.getConsultation(consultationId);
      const consultationData = response.data || response;
      
      setConsultation(consultationData);
      
      // Map medications to prescriptions for compatibility
      const prescriptions = consultationData.medications?.map(med => ({
        medication: med.medication_name || med.name || med,
        dosage: med.dosage || '',
        frequency: med.frequency || '',
        instructions: med.instructions || '',
      })) || consultationData.prescriptions || [];

      // Map tests_ordered to testsOrdered for compatibility
      const testsOrdered = consultationData.tests_ordered?.map(test => ({
        test_name: test.test_name || test.name,
        testName: test.test_name || test.name,
        instructions: test.instructions || '',
        status: test.status || 'Pending'
      })) || consultationData.testsOrdered || [];

      setConsultationData({
        chief_complaint: consultationData.chief_complaint || '',
        history_of_present_illness: consultationData.history_of_present_illness || '',
        review_of_systems: consultationData.review_of_systems || '',
        physical_examination: consultationData.physical_examination || '',
        assessment_and_plan: consultationData.assessment_and_plan || '',
        clinical_notes: consultationData.clinical_notes || consultationData.notes || '',
        vital_signs: consultationData.vital_signs || {},
        symptoms: consultationData.symptoms || [],
        diagnoses: consultationData.diagnoses || [],
        medications: consultationData.medications || [],
        allergies: consultationData.allergies || [],
        // Backward compatibility fields
        prescriptions: prescriptions,
        testsOrdered: testsOrdered,
        referrals: consultationData.referrals || [],
        notes: consultationData.clinical_notes || consultationData.notes || '',
        diagnosis: consultationData.diagnoses?.[0] || consultationData.diagnosis || '',
        follow_up_required: consultationData.follow_up_required || false,
        follow_up_date: consultationData.follow_up_date || null,
        follow_up_notes: consultationData.follow_up_notes || '',
        outcome: consultationData.outcome || '',
        patient_satisfaction_score: consultationData.patient_satisfaction_score || null,
        doctor_notes: consultationData.doctor_notes || '',
      });
      
      setIsConsultationActive(consultationData.status === 'in_progress');
      
      // Load related data
      if (consultationData.documents) setDocuments(consultationData.documents);
      if (consultationData.reminders) setReminders(consultationData.reminders);
      if (consultationData.analytics) setAnalytics(consultationData.analytics);
      
    } catch (error) {
      console.error('Error loading consultation:', error);
      setErrorMessage('Failed to load consultation');
      setSnackbarOpen(true);
    } finally {
      setConsultationLoading(false);
    }
  };

  // Start consultation
  const handleStartConsultation = async () => {
    try {
      // Get patient ID from props or state
      const currentPatientId = patientId || selectedPatient?.id;
      
      if (!currentPatientId) {
        toast.error('Please select a patient before starting consultation');
        setErrorMessage('Patient is required to start consultation');
        setSnackbarOpen(true);
        return;
      }

      if (!consultation) {
        // Create new consultation
        const consultationData = {
          patient: currentPatientId,
          consultation_type: 'in_person',
          scheduled_start: new Date().toISOString(),
          scheduled_end: new Date(Date.now() + 30 * 60000).toISOString(), // 30 minutes
          priority: 'normal',
          status: 'scheduled'
        };
        
        // Add appointment reference if available
        if (appointmentId) {
          consultationData.appointment = appointmentId;
        }
        
        const newConsultation = await consultationService.createConsultation(consultationData);
        
        // Handle different response structures
        const consultationObj = newConsultation.data || newConsultation;
        setConsultation(consultationObj);
        
        // Start the consultation immediately
        const response = await consultationService.startConsultation(consultationObj.id);
        const startedConsultation = response.data?.consultation || response.data || response;
        setConsultation(startedConsultation);
        setIsConsultationActive(true);
      } else {
        // Start existing consultation
        const response = await consultationService.startConsultation(consultation.id);
        const startedConsultation = response.data?.consultation || response.data || response;
        setConsultation(startedConsultation);
        setIsConsultationActive(true);
      }
      
      toast.success('Consultation started successfully!');
    } catch (error) {
      console.error('Error starting consultation:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || error.message || 'Failed to start consultation';
      setErrorMessage(errorMsg);
      setSnackbarOpen(true);
      toast.error(errorMsg);
    }
  };

  // Complete consultation
  const handleCompleteConsultation = async () => {
    if (!consultation?.id) {
      toast.error('No consultation to complete. Please start a consultation first.');
      return;
    }
    
    try {
      // Prepare completion data with all fields - ensure proper formatting
      const prescriptions = consultationData.prescriptions || 
        (consultationData.prescription?.medications || []).map(med => ({
          medication: med.medication_name || med.medication || med,
          dosage: med.dosage || '',
          frequency: med.frequency || '',
          instructions: med.instructions || ''
        }));
      
      const testsOrdered = consultationData.testsOrdered || 
        (consultationData.tests_ordered || []).map(test => ({
          test_name: test.test_name || test.testName || test,
          instructions: test.instructions || '',
          status: test.status || 'Pending'
        }));
      
      const referrals = consultationData.referrals || [];
      
      const completionData = {
        // Core consultation fields
        chief_complaint: consultationData.chief_complaint || '',
        history_of_present_illness: consultationData.history_of_present_illness || '',
        review_of_systems: consultationData.review_of_systems || '',
        physical_examination: consultationData.physical_examination || '',
        assessment_and_plan: consultationData.assessment_and_plan || '',
        clinical_notes: consultationData.clinical_notes || consultationData.notes || '',
        doctor_notes: consultationData.doctor_notes || '',
        outcome: consultationData.outcome || '',
        
        // Vital signs
        vital_signs: consultationData.vital_signs || {},
        
        // Symptoms - ensure it's an array
        symptoms: Array.isArray(consultationData.symptoms) 
          ? consultationData.symptoms 
          : (consultationData.symptoms ? [consultationData.symptoms] : []),
        
        // Diagnoses - ensure it's an array
        diagnoses: consultationData.diagnoses?.length > 0 
          ? consultationData.diagnoses 
          : (consultationData.diagnosis ? [consultationData.diagnosis] : []),
        
        // Medications - properly formatted
        medications: prescriptions.map(pres => ({
          medication_name: pres.medication || pres.medication_name || '',
          dosage: pres.dosage || '',
          frequency: pres.frequency || '',
          instructions: pres.instructions || ''
        })),
        
        // Tests ordered - properly formatted
        tests_ordered: testsOrdered.map(test => ({
          test_name: test.test_name || test.testName || '',
          instructions: test.instructions || '',
          status: test.status || 'Pending'
        })),
        
        // Referrals - properly formatted
        referrals: referrals.map(ref => ({
          referred_to: ref.referred_to || ref.specialist || '',
          reason: ref.reason || ''
        })),
        
        // Allergies
        allergies: consultationData.allergies || [],
        
        // Follow-up
        follow_up_required: consultationData.follow_up_required || false,
        follow_up_date: consultationData.follow_up_date || null,
        follow_up_notes: consultationData.follow_up_notes || '',
        
        // Patient satisfaction
        patient_satisfaction_score: consultationData.patient_satisfaction_score || null,
        
        // Status
        status: 'completed',
        is_completed: true
      };
      
      console.log('🚀 Completing consultation with data:', JSON.stringify(completionData, null, 2));
      
      const response = await consultationService.completeConsultation(consultation.id, completionData);
      
      const completedConsultation = response.data?.consultation || response.data || response;
      setConsultation(completedConsultation);
      setIsConsultationActive(false);
      
      toast.success('Consultation completed successfully!');
      
      // Navigate to consultation history or dashboard
      setTimeout(() => {
        navigate('/doctor-dashboard/consultation-dashboard');
      }, 1500);
    } catch (error) {
      console.error('❌ Error completing consultation:', error);
      const errorMsg = error.response?.data?.detail || 
        error.response?.data?.error || 
        error.response?.data?.message ||
        error.message || 
        'Failed to complete consultation';
      setErrorMessage(errorMsg);
      setSnackbarOpen(true);
      toast.error(errorMsg);
    }
  };

  // Save consultation data
  const handleSaveConsultation = async (silent = false) => {
    if (!consultation?.id) {
      if (!silent) {
        toast.warning('Please start the consultation before saving');
      }
      return;
    }
    
    if (isSaving) {
      return; // Prevent concurrent saves
    }
    
    try {
      setIsSaving(true);
      
      // Prepare save data with all fields - ensure proper formatting
      const prescriptions = consultationData.prescriptions || 
        (consultationData.prescription?.medications || []).map(med => ({
          medication: med.medication_name || med.medication || med,
          dosage: med.dosage || '',
          frequency: med.frequency || '',
          instructions: med.instructions || ''
        }));
      
      const testsOrdered = consultationData.testsOrdered || 
        (consultationData.tests_ordered || []).map(test => ({
          test_name: test.test_name || test.testName || test,
          testName: test.test_name || test.testName || test,
          instructions: test.instructions || '',
          status: test.status || 'Pending'
        }));
      
      const referrals = consultationData.referrals || [];
      
      const saveData = {
        ...consultationData,
        // Ensure prescriptions are properly formatted
        medications: prescriptions.map(pres => ({
          medication_name: pres.medication || pres.medication_name || '',
          dosage: pres.dosage || '',
          frequency: pres.frequency || '',
          instructions: pres.instructions || ''
        })),
        // Ensure tests are properly formatted
        tests_ordered: testsOrdered.map(test => ({
          test_name: test.test_name || test.testName || '',
          instructions: test.instructions || '',
          status: test.status || 'Pending'
        })),
        // Ensure referrals are properly formatted
        referrals: referrals.map(ref => ({
          referred_to: ref.referred_to || ref.specialist || '',
          reason: ref.reason || ''
        })),
        // Sync notes fields
        clinical_notes: consultationData.clinical_notes || consultationData.notes || '',
        notes: consultationData.clinical_notes || consultationData.notes || '',
        // Ensure diagnosis is set
        diagnoses: consultationData.diagnoses?.length > 0 
          ? consultationData.diagnoses 
          : (consultationData.diagnosis ? [consultationData.diagnosis] : []),
      };
      
      const response = await consultationService.updateConsultation(consultation.id, saveData);
      
      const updatedConsultation = response.data || response;
      setConsultation(updatedConsultation);
      setLastSaved(new Date());
      
      if (!silent) {
        toast.success('Consultation saved successfully!');
      }
    } catch (error) {
      console.error('Error saving consultation:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || error.message || 'Failed to save consultation';
      if (!silent) {
        setErrorMessage(errorMsg);
        setSnackbarOpen(true);
        toast.error(errorMsg);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Handle template selection
  const handleTemplateSelect = (template) => {
    if (template.sections) {
      const templateData = { ...consultationData };
      
      // Apply template sections
      Object.keys(template.sections).forEach(section => {
        if (templateData[section] === undefined) {
          templateData[section] = template.sections[section];
        }
      });
      
      // Apply default values
      Object.keys(template.default_values).forEach(field => {
        if (templateData[field] === undefined || templateData[field] === '') {
          templateData[field] = template.default_values[field];
        }
      });
      
      setConsultationData(templateData);
      setTemplateModalOpen(false);
      
      toast.success(`Template "${template.name}" applied successfully!`);
    }
  };

  // Handle document upload
  const handleDocumentUpload = async (file, documentType, title, description) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);
      formData.append('title', title);
      formData.append('description', description);
      
      const response = await consultationService.addDocument(consultation.id, formData);
      setDocuments(prev => [...prev, response.data]);
      
      toast.success('Document uploaded successfully!');
    } catch (error) {
      console.error('Error uploading document:', error);
      setErrorMessage('Failed to upload document');
      setSnackbarOpen(true);
    }
  };

  // Handle reminder scheduling
  const handleScheduleReminder = async (reminderData) => {
    try {
      const response = await consultationService.scheduleReminder(consultation.id, reminderData);
      setReminders(prev => [...prev, response.data]);
      
      toast.success('Reminder scheduled successfully!');
    } catch (error) {
      console.error('Error scheduling reminder:', error);
      setErrorMessage('Failed to schedule reminder');
      setSnackbarOpen(true);
    }
  };

  // Timer effect
  useEffect(() => {
    let interval = null;
    if (isConsultationActive && consultation?.actual_start) {
      interval = setInterval(() => {
        const startTime = new Date(consultation.actual_start);
        const elapsed = Math.floor((new Date() - startTime) / 1000);
        setTimeElapsed(elapsed);
      }, 1000);
    } else {
      setTimeElapsed(0);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isConsultationActive, consultation?.actual_start]);

  // Auto-save effect - save every 30 seconds when consultation is active
  useEffect(() => {
    if (isConsultationActive && consultation?.id) {
      const interval = setInterval(async () => {
        try {
          console.log('🔄 Auto-save triggered...', new Date().toLocaleTimeString());
          setAutoSaveIndicator(true);
          
          // Use latest consultationData via callback
          await handleSaveConsultation(true); // true = silent save
          
          setSaveCount(prev => prev + 1);
          console.log('✅ Auto-save completed successfully', { count: saveCount + 1 });
          
          // Hide indicator after 2 seconds
          setTimeout(() => setAutoSaveIndicator(false), 2000);
        } catch (error) {
          console.error('❌ Auto-save failed:', error);
          setAutoSaveIndicator(false);
        }
      }, 30000); // 30 seconds

      setAutoSaveInterval(interval);
      
      return () => {
        if (interval) clearInterval(interval);
      };
    } else {
      if (autoSaveInterval) {
        clearInterval(autoSaveInterval);
        setAutoSaveInterval(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConsultationActive, consultation?.id]);

  // Test mode - simulate various data scenarios
  useEffect(() => {
    if (testMode && consultation?.id) {
      console.log('🧪 Test Mode: Running data scenarios...');
      
      // Test Scenario 1: Empty fields
      setTimeout(() => {
        console.log('📝 Test Scenario 1: Testing empty fields');
        setConsultationData(prev => ({
          ...prev,
          chief_complaint: '',
          symptoms: [],
          prescriptions: [],
          testsOrdered: [],
          referrals: []
        }));
      }, 5000);

      // Test Scenario 2: Large data
      setTimeout(() => {
        console.log('📝 Test Scenario 2: Testing large data');
        setConsultationData(prev => ({
          ...prev,
          chief_complaint: 'A'.repeat(1000),
          symptoms: Array(20).fill('Test symptom'),
          prescriptions: Array(10).fill({
            medication: 'Test Medication',
            dosage: '500mg',
            frequency: '2x daily',
            instructions: 'Test instructions'
          })
        }));
      }, 10000);

      // Test Scenario 3: Special characters
      setTimeout(() => {
        console.log('📝 Test Scenario 3: Testing special characters');
        setConsultationData(prev => ({
          ...prev,
          chief_complaint: 'Special chars: !@#$%^&*()_+{}|:<>?',
          diagnoses: ['Diagnosis with "quotes" & <tags>']
        }));
      }, 15000);
    }
  }, [testMode, consultation?.id]);

  // Format time
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Animation variants for framer-motion
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  const cardVariants = {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    hover: { scale: 1.02, transition: { duration: 0.2 } }
  };

  const tabVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 20 }
  };

  if (consultationLoading) {
    return (
      <motion.div
        initial="initial"
        animate="animate"
        variants={pageVariants}
        style={{ minHeight: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Loading consultation...
          </Typography>
        </Box>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial="initial"
      animate="animate"
      variants={pageVariants}
      transition={{ duration: 0.3 }}
    >
      <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" gutterBottom>
              Enhanced Consultation
            </Typography>
            {consultation && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <StatusChip 
                  label={consultation.status_display} 
                  status={consultation.status}
                  icon={<CheckCircleIcon />}
                />
                <PriorityChip 
                  label={consultation.priority_display} 
                  priority={consultation.priority}
                />
                {consultation.is_emergency && (
                  <Chip 
                    label="Emergency" 
                    color="error" 
                    icon={<WarningIcon />}
                  />
                )}
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              {!isConsultationActive && (
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    variant="contained"
                    startIcon={<StartIcon />}
                    onClick={handleStartConsultation}
                    disabled={consultationLoading}
                  >
                    Start Consultation
                  </Button>
                </motion.div>
              )}
              {isConsultationActive && (
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<StopIcon />}
                    onClick={handleCompleteConsultation}
                    disabled={consultationLoading}
                  >
                    Complete Consultation
                  </Button>
                </motion.div>
              )}
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="outlined"
                  startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                  onClick={() => handleSaveConsultation(false)}
                  disabled={consultationLoading || isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </motion.div>
              {lastSaved && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    Last saved: {lastSaved.toLocaleTimeString()}
                  </Typography>
                </motion.div>
              )}
              {autoSaveIndicator && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: [0, 1, 1, 0], x: 0 }}
                  transition={{ duration: 2 }}
                  style={{ marginLeft: 8 }}
                >
                  <Chip
                    label="Auto-saving..."
                    size="small"
                    color="info"
                    sx={{ 
                      animation: 'pulse 1s infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.5 }
                      }
                    }}
                  />
                </motion.div>
              )}
              {testMode && (
                <Chip
                  label="🧪 Test Mode"
                  size="small"
                  color="warning"
                  onClick={() => setTestMode(false)}
                  sx={{ ml: 1, cursor: 'pointer' }}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Consultation Timer */}
      <AnimatePresence>
        {isConsultationActive && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Paper 
              sx={{ 
                p: 2, 
                mb: 3, 
                bgcolor: alpha(theme.palette.success.main, 0.1),
                border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                >
                  <AccessTimeIcon color="success" />
                </motion.div>
                <Typography variant="h6" color="success.main">
                  Consultation in Progress: {formatTime(timeElapsed)}
                </Typography>
                {autoSaveIndicator && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <Chip
                      label={`Auto-saved ${saveCount} times`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  </motion.div>
                )}
              </Box>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Patient Selection - Show if no patient selected */}
      {!patientId && !selectedPatient && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: alpha(theme.palette.info.main, 0.05) }}>
          <Typography variant="h6" gutterBottom>
            Select Patient
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Choose Patient</InputLabel>
            <Select
              value={selectedPatient?.id || ''}
              onChange={(e) => {
                const patientId = e.target.value;
                dispatch(getPatientDetails(patientId));
              }}
              disabled={loading}
            >
              {loading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 2 }} />
                  Loading patients...
                </MenuItem>
              ) : (
                patients.map((patient) => (
                  <MenuItem key={patient.id} value={patient.id}>
                    {patient.name} - {patient.phone_number || patient.email}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Paper>
      )}

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column - Consultation Steps */}
        <Grid item xs={12} md={4}>
          <motion.div
            variants={cardVariants}
            initial="initial"
            animate="animate"
            transition={{ delay: 0.1 }}
          >
            <StyledCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Consultation Progress
              </Typography>
              <Stepper activeStep={activeStep} orientation="vertical">
                {consultationSteps.map((step, index) => (
                  <Step key={step.label}>
                    <StepLabel>{step.label}</StepLabel>
                    <StepContent>
                      <Typography variant="body2" color="text.secondary">
                        {step.description}
                      </Typography>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </StyledCard>
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            variants={cardVariants}
            initial="initial"
            animate="animate"
            transition={{ delay: 0.2 }}
          >
            <StyledCard sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<DescriptionIcon />}
                  onClick={() => setTemplateModalOpen(true)}
                >
                  Use Template
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AttachFileIcon />}
                  onClick={() => setDocumentModalOpen(true)}
                >
                  Add Document
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ScheduleIcon />}
                  onClick={() => setReminderModalOpen(true)}
                >
                  Schedule Reminder
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AnalyticsIcon />}
                  onClick={() => setAnalyticsModalOpen(true)}
                >
                  View Analytics
                </Button>
              </Box>
            </CardContent>
          </StyledCard>
          </motion.div>
        </Grid>

        {/* Right Column - Consultation Content */}
        <Grid item xs={12} md={8}>
          <motion.div
            variants={cardVariants}
            initial="initial"
            animate="animate"
            transition={{ delay: 0.3 }}
          >
            <StyledCard>
            <CardContent>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                  <Tab label="Chief Complaint" />
                  <Tab label="History & Exam" />
                  <Tab label="Assessment & Plan" />
                  <Tab label="Documentation" />
                  <Tab label="Follow-up" />
                </Tabs>
              </Box>

              {/* Tab Panels */}
              <TabPanel value={tabValue} index={0}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Chief Complaint"
                      value={consultationData.chief_complaint}
                      onChange={(e) => setConsultationData(prev => ({
                        ...prev,
                        chief_complaint: e.target.value
                      }))}
                      placeholder="Describe the primary reason for the patient's visit..."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <SymptomsForm
                      symptoms={consultationData.symptoms}
                      setSymptoms={(symptoms) =>
                        setConsultationData(prev => ({ ...prev, symptoms }))
                      }
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="History of Present Illness"
                      value={consultationData.history_of_present_illness}
                      onChange={(e) => setConsultationData(prev => ({
                        ...prev,
                        history_of_present_illness: e.target.value
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Review of Systems"
                      value={consultationData.review_of_systems}
                      onChange={(e) => setConsultationData(prev => ({
                        ...prev,
                        review_of_systems: e.target.value
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Physical Examination"
                      value={consultationData.physical_examination}
                      onChange={(e) => setConsultationData(prev => ({
                        ...prev,
                        physical_examination: e.target.value
                      }))}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Assessment and Plan"
                      value={consultationData.assessment_and_plan}
                      onChange={(e) => setConsultationData(prev => ({
                        ...prev,
                        assessment_and_plan: e.target.value
                      }))}
                      placeholder="Describe your assessment and treatment plan..."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <DiagnosisManager
                      diagnoses={consultationData.diagnoses || []}
                      setDiagnoses={(diagnoses) =>
                        setConsultationData(prev => ({ 
                          ...prev, 
                          diagnoses,
                          diagnosis: diagnoses.length > 0 ? diagnoses[0] : ''
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <PrescriptionTab
                      prescriptions={consultationData.prescriptions || []}
                      setPrescriptions={(prescriptions) =>
                        setConsultationData(prev => ({ ...prev, prescriptions }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TestsForm
                      testsOrdered={consultationData.testsOrdered || []}
                      setTestsOrdered={(testsOrdered) =>
                        setConsultationData(prev => ({ ...prev, testsOrdered }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ReferralsForm
                      referrals={consultationData.referrals || []}
                      setReferrals={(referrals) =>
                        setConsultationData(prev => ({ ...prev, referrals }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Outcome"
                      value={consultationData.outcome}
                      onChange={(e) => setConsultationData(prev => ({
                        ...prev,
                        outcome: e.target.value
                      }))}
                      placeholder="Consultation outcome..."
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Clinical Notes"
                      value={consultationData.clinical_notes}
                      onChange={(e) => setConsultationData(prev => ({
                        ...prev,
                        clinical_notes: e.target.value
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Doctor Notes"
                      value={consultationData.doctor_notes}
                      onChange={(e) => setConsultationData(prev => ({
                        ...prev,
                        doctor_notes: e.target.value
                      }))}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={4}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Follow-up Required</InputLabel>
                      <Select
                        value={consultationData.follow_up_required}
                        onChange={(e) => setConsultationData(prev => ({
                          ...prev,
                          follow_up_required: e.target.value
                        }))}
                      >
                        <MenuItem value={false}>No</MenuItem>
                        <MenuItem value={true}>Yes</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {consultationData.follow_up_required && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="datetime-local"
                        label="Follow-up Date"
                        value={consultationData.follow_up_date}
                        onChange={(e) => setConsultationData(prev => ({
                          ...prev,
                          follow_up_date: e.target.value
                        }))}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Follow-up Notes"
                      value={consultationData.follow_up_notes}
                      onChange={(e) => setConsultationData(prev => ({
                        ...prev,
                        follow_up_notes: e.target.value
                      }))}
                    />
                  </Grid>
                </Grid>
              </TabPanel>
            </CardContent>
          </StyledCard>
          </motion.div>
        </Grid>
      </Grid>

      {/* Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Test Mode Toggle (Development) */}
      {process.env.NODE_ENV === 'development' && (
        <Box sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setTestMode(!testMode)}
            sx={{ 
              bgcolor: testMode ? 'warning.light' : 'background.paper',
              '&:hover': { bgcolor: testMode ? 'warning.main' : 'action.hover' }
            }}
          >
            {testMode ? '🧪 Test Mode ON' : '🧪 Test Mode'}
          </Button>
        </Box>
      )}
    </Box>
    </motion.div>
  );
};

export default EnhancedConsultation;









